import { Link } from "react-router-dom";
import { ArrowRight, Shield, TrendingUp, Users, Building2, Calculator, Home as HomeIcon, FileCheck, Briefcase, Key, FileText, UserCheck, LineChart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Layout from "@/components/Layout";

const HERO_IMAGE = "https://images.unsplash.com/photo-1759684741287-e03cf2169f9a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxuYW50ZXMlMjBjaXR5JTIwYXJjaGl0ZWN0dXJlJTIwbGFuZG1hcmt8ZW58MHx8fHwxNzcyNjM2MDY3fDA&ixlib=rb-4.1.0&q=85";
const TRUST_IMAGE = "https://images.unsplash.com/photo-1549923746-c502d488b3ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjByZWFsJTIwZXN0YXRlJTIwYWdlbnQlMjBoYW5kc2hha2UlMjBjbGllbnR8ZW58MHx8fHwxNzcyNjM2MDg1fDA&ixlib=rb-4.1.0&q=85";

const dispositifs = [
  {
    id: "denormandie",
    title: "Denormandie",
    description: "Jusqu'à 21% de réduction d'impôt pour la rénovation de logements anciens. Dispositif prolongé jusqu'en 2027.",
    icon: HomeIcon,
    color: "text-growth",
  },
  {
    id: "jeanbrun",
    title: "Jeanbrun 2026",
    description: "Nouveau dispositif: amortissement jusqu'à 5,5%/an pour les bailleurs privés. Neuf et ancien rénové.",
    icon: TrendingUp,
    color: "text-clay",
  },
  {
    id: "locavantages",
    title: "Loc'Avantages",
    description: "Jusqu'à 65% de réduction sur les loyers perçus avec convention Anah. Prolongé jusqu'en 2027.",
    icon: FileCheck,
    color: "text-harbor",
  },
  {
    id: "lmnp",
    title: "LMNP",
    description: "Statut fiscal avantageux pour la location meublée avec amortissement du bien.",
    icon: Building2,
    color: "text-harbor",
  },
];

const services = [
  { icon: Briefcase, title: "Conseil", description: "Analyse personnalisée de votre situation" },
  { icon: HomeIcon, title: "Choix du bien", description: "Sélection adaptée à vos objectifs" },
  { icon: FileText, title: "Financement", description: "Accompagnement crédit et montage" },
  { icon: UserCheck, title: "Notaire", description: "Suivi jusqu'à la signature" },
  { icon: Key, title: "Gestion", description: "Mise en location et gestion locative" },
  { icon: LineChart, title: "Suivi patrimonial", description: "Accompagnement dans la durée" },
];

const Home = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section
        className="hero-section relative"
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 hero-overlay" />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl text-white">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 animate-fade-in-up">
              Gérez et développez<br />
              <span className="text-clay">votre patrimoine</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-8 animate-fade-in-up animation-delay-200">
              Inter'med Immobilier vous accompagne dans vos projets d'investissement locatif
              et résidentiel à Nantes. Découvrez les dispositifs fiscaux adaptés à votre situation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-300">
              <Link to="/simulateur">
                <Button size="lg" className="bg-clay hover:bg-clay/90 text-white w-full sm:w-auto" data-testid="hero-cta-simulateur">
                  Simuler ma réduction d'impôt
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/catalogue">
                <Button size="lg" variant="outline" className="bg-white/10 border-white text-white hover:bg-white/20 w-full sm:w-auto" data-testid="hero-cta-catalogue">
                  Découvrir nos biens
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-harbor/5" data-testid="services-section">
        <div className="container-custom">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl text-harbor mb-3">
              Un accompagnement complet
            </h2>
            <p className="text-muted-foreground">
              Du conseil à la gestion, nous sommes à vos côtés à chaque étape
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="text-center p-5 rounded-lg bg-white shadow-sm hover:shadow-md transition-all"
                data-testid={`service-${index}`}
              >
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-harbor/10 flex items-center justify-center">
                  <service.icon className="text-harbor" size={26} />
                </div>
                <h3 className="font-semibold text-harbor text-sm mb-2">{service.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dispositifs Section - Denormandie en avant */}
      <section className="section-spacing bg-gradient-to-b from-harbor/5 to-growth/5" data-testid="dispositifs-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl text-harbor mb-4">
              Dispositifs de Défiscalisation
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Réduisez vos impôts grâce à l'investissement immobilier locatif
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {dispositifs.map((dispositif, index) => (
              <Card
                key={dispositif.id}
                className="dispositif-card p-6 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`dispositif-card-${dispositif.id}`}
              >
                <CardContent className="p-0">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-md bg-gray-50 ${dispositif.color}`}>
                      <dispositif.icon size={28} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-xl text-harbor mb-2">{dispositif.title}</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {dispositif.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/dispositifs">
              <Button variant="outline" className="border-harbor text-harbor hover:bg-harbor hover:text-white" data-testid="voir-dispositifs">
                Voir tous les dispositifs
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Simulateur CTA Section */}
      <section className="py-20 bg-harbor" data-testid="simulateur-cta-section">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1 text-white">
              <h2 className="font-heading text-3xl md:text-4xl mb-4">
                Calculez votre réduction d'impôt
              </h2>
              <p className="text-white/80 text-lg mb-6">
                Utilisez notre simulateur gratuit pour estimer votre économie fiscale
                en fonction du dispositif choisi et de votre investissement.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-white/90">
                  <div className="w-2 h-2 rounded-full bg-clay" />
                  Simulation personnalisée en quelques clics
                </li>
                <li className="flex items-center gap-3 text-white/90">
                  <div className="w-2 h-2 rounded-full bg-clay" />
                  Comparaison des différents dispositifs
                </li>
                <li className="flex items-center gap-3 text-white/90">
                  <div className="w-2 h-2 rounded-full bg-clay" />
                  Conseils d'expert offerts
                </li>
              </ul>
              <Link to="/simulateur">
                <Button size="lg" className="bg-clay hover:bg-clay/90 text-white" data-testid="simulateur-cta-button">
                  Lancer la simulation
                  <Calculator className="ml-2" size={20} />
                </Button>
              </Link>
            </div>
            <div className="flex-1 max-w-md">
              <div className="bg-white rounded-md p-8 shadow-2xl">
                <div className="text-center">
                  <div className="text-5xl font-heading text-harbor mb-2">-21%</div>
                  <div className="text-muted-foreground">Réduction d'impôt Denormandie</div>
                </div>
                <div className="border-t border-gray-100 my-6" />
                <div className="space-y-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Investissement type</span>
                    <span className="font-semibold">200 000 €</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Économie potentielle</span>
                    <span className="font-semibold text-growth">42 000 €</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Durée engagement</span>
                    <span className="font-semibold">12 ans</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Catalogue CTA */}
      <section className="py-20 bg-harbor/5" data-testid="catalogue-teaser">
        <div className="container-custom">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl text-harbor mb-3">
              Découvrez nos biens
            </h2>
            <p className="text-muted-foreground">
              Une sélection de biens adaptés à chaque projet
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Link to="/catalogue?catalogue=investissement" className="block">
              <Card className="h-full hover:shadow-xl transition-all cursor-pointer border-2 border-transparent hover:border-harbor/30" data-testid="catalogue-investissement">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-harbor/10 flex items-center justify-center">
                    <Building2 className="text-harbor" size={32} />
                  </div>
                  <h3 className="font-heading text-xl text-harbor mb-3">Investissement Locatif</h3>
                  <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                    Biens éligibles Denormandie, Pinel, LMNP pour réduire vos impôts
                  </p>
                  <Button variant="outline" className="border-harbor text-harbor hover:bg-harbor hover:text-white">
                    Voir le catalogue
                  </Button>
                </CardContent>
              </Card>
            </Link>
            <Link to="/catalogue?catalogue=residence_principale" className="block">
              <Card className="h-full hover:shadow-xl transition-all cursor-pointer border-2 border-transparent hover:border-growth/30" data-testid="catalogue-residence">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-growth/10 flex items-center justify-center">
                    <HomeIcon className="text-growth" size={32} />
                  </div>
                  <h3 className="font-heading text-xl text-harbor mb-3">Résidence Principale</h3>
                  <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                    Appartements et maisons à Nantes et environs
                  </p>
                  <Button variant="outline" className="border-growth text-growth hover:bg-growth hover:text-white">
                    Voir le catalogue
                  </Button>
                </CardContent>
              </Card>
            </Link>
            <Link to="/catalogue?catalogue=accession_aidee" className="block">
              <Card className="h-full hover:shadow-xl transition-all cursor-pointer border-2 border-transparent hover:border-clay/30" data-testid="catalogue-accession">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-clay/10 flex items-center justify-center">
                    <Key className="text-clay" size={32} />
                  </div>
                  <h3 className="font-heading text-xl text-harbor mb-3">Accession Aidée</h3>
                  <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                    BRS, Location-Accession, TVA 5.5% pour primo-accédants
                  </p>
                  <Button variant="outline" className="border-clay text-clay hover:bg-clay hover:text-white">
                    Voir le catalogue
                  </Button>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Avantages Section */}
      <section className="section-spacing bg-gradient-to-br from-harbor/5 via-white to-growth/5" data-testid="avantages-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src={TRUST_IMAGE}
                alt="Accompagnement professionnel"
                className="rounded-md shadow-xl"
              />
            </div>
            <div>
              <h2 className="font-heading text-3xl md:text-4xl text-harbor mb-6">
                Pourquoi choisir Inter'med Immobilier ?
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4" data-testid="avantage-0">
                  <div className="p-3 rounded-md bg-harbor/10 text-harbor h-fit">
                    <Shield size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-harbor mb-1">Expertise Locale</h3>
                    <p className="text-muted-foreground">Connaissance approfondie du marché nantais et de ses opportunités.</p>
                  </div>
                </div>
                <div className="flex gap-4" data-testid="avantage-1">
                  <div className="p-3 rounded-md bg-harbor/10 text-harbor h-fit">
                    <Calculator size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-harbor mb-1">Optimisation Fiscale</h3>
                    <p className="text-muted-foreground">Stratégies sur-mesure pour maximiser vos avantages fiscaux.</p>
                  </div>
                </div>
                <div className="flex gap-4" data-testid="avantage-2">
                  <div className="p-3 rounded-md bg-harbor/10 text-harbor h-fit">
                    <Users size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-harbor mb-1">Accompagnement Complet</h3>
                    <p className="text-muted-foreground">Du conseil au suivi patrimonial, nous sommes à vos côtés.</p>
                  </div>
                </div>
              </div>
              <Link to="/contact" className="inline-block mt-8">
                <Button variant="outline" className="border-harbor text-harbor hover:bg-harbor hover:text-white" data-testid="contact-cta">
                  Prendre rendez-vous
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
